// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React from "react";
import { AxiosError } from "axios";
import { createApi } from "../../../utils/createApi";
import { getRefreshToken } from "../../../utils/getRefreshToken";
import { NavigateFunction } from "react-router-dom";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { AxisTab } from "../AxisTab";
import { getNumbersAfterEditCamera } from "../EditCamera";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { getNumbersAfterSites } from "./urlUtils";
import {
  AXIS_RULE_JOB_PENDING_MESSAGE,
  AxisRuleJobPollOptions,
  AxisRuleJobStatusResponse,
  pollAxisRuleJob,
  summarizeAxisRuleJob,
  useAxisDeviceJobStore,
} from "./axisRuleJob";

/**
 * WireGuard sites (ENG-2822): admin can't reach the camera, so it answers 202
 * and runs the change as a Job on the site's network. Show it as pending on
 * the tab, poll for the outcome, and show that — the rule list can't be
 * re-read on these sites (it would 409), so the outcome is what the operator
 * gets.
 */
export const followAxisRuleJob = async (
  cameraId: string | number,
  siteId: string | number,
  dispatch: AxisRuleJobStatusResponse,
  pollOptions: AxisRuleJobPollOptions = {}
) => {
  const { setRuleJob } = useAxisDeviceJobStore.getState();
  setRuleJob(cameraId, {
    phase: "pending",
    severity: "info",
    message: AXIS_RULE_JOB_PENDING_MESSAGE,
    details: [],
  });
  const outcome = await pollAxisRuleJob(siteId, dispatch, pollOptions);
  if (outcome === null) return;
  setRuleJob(cameraId, summarizeAxisRuleJob(outcome, cameraId));
};

export const handleAddAxisActionRule = (
  cameraId: string,
  navigate: NavigateFunction,
  setBackdropLoadingScreenOn: Dispatcher<boolean>,
  setActionRulesData: any,
  setActionRulesTitlesArray: Dispatcher<any>,
  setRecipientsConfiguredData: any,
  setTitlesArray: any,
  setActiveTab: any,
  recipientsConfiguredData: any,
  titlesArray: any,
  actionRulesData: any,
  actionRulesTitlesArray: any,
  applicationsConfiguredData: any,
  cameraGeneralInfo: any,
  axisFinished: any,
  pollOptions: AxisRuleJobPollOptions = {}
) => {
  const AxiosUI = createApi("");

  const retryFunction = () => {
    handleAddAxisActionRule(
      cameraId,
      navigate,
      setBackdropLoadingScreenOn,
      setActionRulesData,
      setActionRulesTitlesArray,
      setRecipientsConfiguredData,
      setTitlesArray,
      setActiveTab,
      recipientsConfiguredData,
      titlesArray,
      actionRulesData,
      actionRulesTitlesArray,
      applicationsConfiguredData,
      cameraGeneralInfo,
      axisFinished,
      pollOptions
    );
  };

  AxiosUI.post(
    `/axis/${getNumbersAfterEditCamera(location.href)}/action_rule/`,
    "{}"
  ).then(
    (response: any) => {
      if (response.status === 202) {
        setBackdropLoadingScreenOn(false);
        void followAxisRuleJob(
          getNumbersAfterEditCamera(location.href) || cameraId,
          getNumbersAfterSites(location.href),
          response.data as AxisRuleJobStatusResponse,
          pollOptions
        );
      } else if (
        response.data[0] &&
        response.data[0].message &&
        response.data[0].message.includes("Error retrieving")
      ) {
        setBackdropLoadingScreenOn(false);
        alert("Problem adding action rule, please try again");
      } else {
        setActionRulesData(response.data);
        const newActionRulesTitlesArray = ["Name"];
        response.data.forEach((object: any) => {
          object.additional_fields.forEach((obj: any) => {
            if (
              obj.id === "host" &&
              !newActionRulesTitlesArray.includes("Host")
            ) {
              newActionRulesTitlesArray.push("Host");
            }
            if (
              obj.id === "port" &&
              !newActionRulesTitlesArray.includes("Port")
            ) {
              newActionRulesTitlesArray.push("Port");
            }
            if (
              obj.id === "email_to" &&
              !newActionRulesTitlesArray.includes("E-mail to")
            ) {
              newActionRulesTitlesArray.push("E-mail to");
            }
            if (
              obj.id === "email_from" &&
              !newActionRulesTitlesArray.includes("E-mail from")
            ) {
              newActionRulesTitlesArray.push("E-mail from");
            }
            if (
              obj.id === "message" &&
              !newActionRulesTitlesArray.includes("Message")
            ) {
              newActionRulesTitlesArray.push("Message");
            }
            if (
              obj.id === "enabled" &&
              !newActionRulesTitlesArray.includes("Enabled")
            ) {
              newActionRulesTitlesArray.push("Enabled");
            }
            if (
              obj.id === "profile_selected" &&
              !newActionRulesTitlesArray.includes("Profile")
            ) {
              newActionRulesTitlesArray.push("Profile");
            }
          });
        });
        const orderedFinalTitles = ["Name"];
        if (!orderedFinalTitles.includes("Host")) {
          orderedFinalTitles.push("Host");
        }
        if (!orderedFinalTitles.includes("Port")) {
          orderedFinalTitles.push("Port");
        }
        if (!orderedFinalTitles.includes("E-mail to")) {
          orderedFinalTitles.push("E-mail to");
        }
        if (!orderedFinalTitles.includes("E-mail from")) {
          orderedFinalTitles.push("E-mail from");
        }
        if (!orderedFinalTitles.includes("Message")) {
          orderedFinalTitles.push("Message");
        }
        if (!orderedFinalTitles.includes("Enabled")) {
          orderedFinalTitles.push("Enabled");
        }
        if (!orderedFinalTitles.includes("Profile")) {
          orderedFinalTitles.push("Profile");
        }
        orderedFinalTitles.push("");
        setActionRulesTitlesArray(orderedFinalTitles);
        setBackdropLoadingScreenOn(false);
        setActiveTab(<></>);
        setActiveTab(
          <AxisTab
            recipientsConfiguredData={recipientsConfiguredData}
            titlesArray={titlesArray}
            navigate={navigate}
            setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
            setRecipientsConfiguredData={setRecipientsConfiguredData}
            setTitlesArray={setTitlesArray}
            actionRulesData={response.data}
            setActionRulesData={setActionRulesData}
            actionRulesTitlesArray={orderedFinalTitles}
            setActionRulesTitlesArray={setActionRulesTitlesArray}
            setActiveTab={setActiveTab}
            applicationsConfiguredData={applicationsConfiguredData}
            cameraGeneralInfo={cameraGeneralInfo}
            axisFinished={axisFinished}
          />
        );
      }
    },
    (reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        setBackdropLoadingScreenOn(false);
        // The API explains why (e.g. which sensor a multi-sensor camera needs);
        // fall back to the generic text when it doesn't.
        const data = reason.response?.data as
          | Array<{ message?: string }>
          | undefined;
        const apiMessage = Array.isArray(data) ? data[0]?.message : undefined;
        alert(apiMessage || "Problem adding action rule, please try again");
      });
    }
  );
};
